import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useAppContext } from "../hooks/useAppContext";
import UserProfile from "./SVGIcons/UserProfile";
import ThemeSlider from "./ThemeSlider";
import SignInOutButton from "./SignInOutButton";
import SelectLocale from "./SelectLocale";
import Divider from "./ui/Divider";
import ModalHowToUse from "./ModalHowToUse";
import ModalPrivacy from "./ModalPrivacy";
import { getTranslation } from "../locales/getTranslation";
import SelectTop from "./SelectTop";
import useBoundStore from "../store/useBoundStore";
import ModalTestQuestion from "./ModalTestQuestion";

interface ModalSettingsProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  modalContent: JSX.Element | null;
  setModalContent: Dispatch<SetStateAction<JSX.Element | null>>;
  openSettingsModal: boolean;
  setOpenSettingsModal: Dispatch<SetStateAction<boolean>>;
  triggerRef?: React.RefObject<HTMLElement>;
}
const ModalSettings = ({
  openSettingsModal,
  setOpenSettingsModal,
  triggerRef,
  openModal,
  setOpenModal,
  modalContent,
  setModalContent,
}: ModalSettingsProps) => {
  const { brand, locale } = useAppContext();
  const user = useBoundStore((state) => state.user);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (openModal) {
        return;
      }

      // Don't close if clicking the modal content
      if (modalRef.current && modalRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking the trigger button
      if (triggerRef?.current && triggerRef.current.contains(target)) {
        return;
      }

      setOpenSettingsModal(false);
    };

    if (openSettingsModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openSettingsModal, openModal]);

  return (
    <>
      <div
        className="inline-flex w-full select-none flex-col items-start justify-start gap-5 rounded-[20px] bg-grayscale-solid-elevate-level1 p-5 shadow-[0px_10px_30px_0px_rgba(0,0,0,0.12)]"
        ref={modalRef}
      >
        {user && (
          <div className="inline-flex w-full items-center gap-4">
            <UserProfile className="h-10 w-10 fill-grayscale-solid-g10" />
            <div className="flex flex-col items-start justify-start gap-3.5">
              <span className="text-left font-medium leading-tight text-grayscale-solid-g10">
                {user?.name}
              </span>
              <span className="text-left text-xs font-normal leading-none text-grayscale-solid-g8">
                {user?.email}
              </span>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col gap-2.5">
          <div className="inline-flex w-full flex-col items-start justify-start gap-2.5 md:pl-3">
            <div className="inline-flex h-9 w-full items-center justify-between">
              <span className="justify-start text-xs font-medium leading-none text-grayscale-solid-g10">
                {getTranslation(locale, "Theme")}
              </span>
              <ThemeSlider />
            </div>
          </div>

          <div className="inline-flex w-full flex-col items-start justify-start gap-2.5 md:pl-3">
            <div className="inline-flex h-9 w-full items-center justify-between gap-2.5">
              <span className="justify-start text-xs font-medium leading-none text-grayscale-solid-g10">
                {getTranslation(locale, "Language")}
              </span>
              <SelectLocale />
            </div>
          </div>

          {(import.meta.env.MODE === "dev" ||
            import.meta.env.MODE === "localhost") && (
            <div className="inline-flex w-full flex-col items-start justify-start gap-2.5 pl-3">
              <div className="inline-flex h-9 w-full items-center justify-between">
                <span className="justify-start text-xs font-medium leading-none text-grayscale-solid-g10">
                  {getTranslation(locale, "Documents")}
                </span>
                <SelectTop />
              </div>
            </div>
          )}
        </div>
        {(import.meta.env.MODE === "dev" ||
          import.meta.env.MODE === "localhost") && (
          <button
            className="inline-flex h-7 w-full items-center justify-center rounded-[300px] bg-blue-solid-blue6 px-4 text-sm font-medium text-white hover:bg-blue-solid-blue5"
            onClick={() => {
              setOpenModal(true);
              setModalContent(
                <ModalTestQuestion setOpenModal={setOpenModal} />,
              );
            }}
          >
            {getTranslation(locale, "Run Test")}
          </button>
        )}

        <SignInOutButton
          type={user ? "Sign Out" : "Sign In"}
          setOpenSettingsModal={setOpenSettingsModal}
        />

        <ul className="inline-flex w-full items-center justify-center gap-5 pl-3">
          <li
            className="cursor-pointer text-xs font-medium leading-none text-grayscale-solid-g10 hover:underline"
            onClick={() => {
              setOpenModal(true);
              setModalContent(<ModalHowToUse />);
            }}
          >
            {getTranslation(locale, "How to Use")}
          </li>
          <Divider />
          <li
            className="cursor-pointer text-xs font-medium leading-none text-grayscale-solid-g10 hover:underline"
            onClick={() => {
              setOpenModal(true);
              setModalContent(<ModalPrivacy />);
            }}
          >
            {getTranslation(locale, "Privacy Notice")}
          </li>
        </ul>
      </div>
    </>
  );
};

export default ModalSettings;
